// Enhanced Visual Effects utility for gamification
import soundEffects from './soundEffects';

// Screen shake effect
export const createScreenShake = (element, intensity = 5, duration = 300) => {
  if (!element) return;
  
  const originalTransform = element.style.transform;
  const originalTransition = element.style.transition;
  
  element.style.transition = 'none';
  
  let startTime = Date.now();
  const shake = () => {
    const elapsed = Date.now() - startTime;
    if (elapsed < duration) {
      const x = (Math.random() - 0.5) * intensity * 2;
      const y = (Math.random() - 0.5) * intensity * 2;
      element.style.transform = `translate(${x}px, ${y}px)`;
      requestAnimationFrame(shake);
    } else {
      element.style.transform = originalTransform;
      element.style.transition = originalTransition;
    }
  };
  
  shake();
};

// Glow effect
export const createGlow = (element, color = '#3b82f6', duration = 1000, intensity = 1.0) => {
  if (!element) return;
  
  const originalBoxShadow = element.style.boxShadow;
  const glowColor = color;
  
  // Adjust glow intensity (lower intensity = less bright)
  const opacity1 = Math.min(0.3 * intensity, 0.3);
  const opacity2 = Math.min(0.2 * intensity, 0.2);
  const opacity3 = Math.min(0.1 * intensity, 0.1);
  
  // Convert hex to rgba for opacity control
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  element.style.transition = 'box-shadow 0.3s ease';
  element.style.boxShadow = `0 0 15px ${hexToRgba(glowColor, opacity1)}, 0 0 30px ${hexToRgba(glowColor, opacity2)}, 0 0 45px ${hexToRgba(glowColor, opacity3)}`;
  
  setTimeout(() => {
    element.style.boxShadow = originalBoxShadow;
  }, duration);
};

// Pulse effect
export const createPulse = (element, scale = 1.1, duration = 300) => {
  if (!element) return;
  
  // Ensure element remains clickable
  const originalPointerEvents = element.style.pointerEvents;
  element.style.pointerEvents = 'auto';
  
  const originalTransform = element.style.transform;
  const originalTransition = element.style.transition;
  
  element.style.transition = `transform ${duration}ms ease-in-out`;
  element.style.transform = `scale(${scale})`;
  
  setTimeout(() => {
    element.style.transform = originalTransform;
    setTimeout(() => {
      element.style.transition = originalTransition;
      // Restore original pointer-events if it wasn't set before
      if (!originalPointerEvents) {
        element.style.pointerEvents = '';
      } else {
        element.style.pointerEvents = originalPointerEvents;
      }
    }, duration);
  }, duration);
};

// Ripple effect
export const createRipple = (event, color = '#3b82f6') => {
  // Safety checks
  if (!event) return;
  if (!event.currentTarget) return;
  
  const button = event.currentTarget;
  if (!button || typeof button.getBoundingClientRect !== 'function') return;
  
  try {
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = (event.clientX || rect.left + rect.width / 2) - rect.left - size / 2;
    const y = (event.clientY || rect.top + rect.height / 2) - rect.top - size / 2;
    
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.style.position = 'absolute';
    ripple.style.borderRadius = '50%';
    ripple.style.background = color;
    ripple.style.opacity = '0.5';
    ripple.style.transform = 'scale(0)';
    ripple.style.transition = 'transform 0.6s ease, opacity 0.6s ease';
    ripple.style.pointerEvents = 'none';
    ripple.style.zIndex = '1';
    
    // Ensure button can receive clicks
    const originalPosition = button.style.position;
    const originalOverflow = button.style.overflow;
    const originalZIndex = button.style.zIndex;
    
    button.style.position = 'relative';
    button.style.overflow = 'hidden';
    button.style.zIndex = 'auto';
    button.appendChild(ripple);
    
    setTimeout(() => {
      ripple.style.transform = 'scale(2)';
      ripple.style.opacity = '0';
    }, 10);
    
    setTimeout(() => {
      ripple.remove();
      // Restore original styles if they weren't set before
      if (!originalPosition) button.style.position = '';
      if (!originalOverflow) button.style.overflow = '';
      if (!originalZIndex) button.style.zIndex = '';
    }, 600);
  } catch (error) {
    console.warn('Ripple effect error:', error);
  }
};

// Sparkle effect - now uses InvestNYou logo instead of coins
export const createSparkle = (element, count = 10) => {
  if (!element) return;
  
  const rect = element.getBoundingClientRect();
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '9999';
  document.body.appendChild(container);
  
  for (let i = 0; i < count; i++) {
    const sparkle = document.createElement('img');
    const size = Math.random() * 20 + 16; // Logo size between 16-36px
    const x = rect.left + Math.random() * rect.width;
    const y = rect.top + Math.random() * rect.height;
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * 100 + 50;
    
    // Use InvestNYou logo
    sparkle.src = '/LogoGurt.jpg';
    sparkle.alt = 'InvestNYou';
    sparkle.style.position = 'absolute';
    sparkle.style.left = `${x}px`;
    sparkle.style.top = `${y}px`;
    sparkle.style.width = `${size}px`;
    sparkle.style.height = `${size}px`;
    sparkle.style.borderRadius = '50%';
    sparkle.style.objectFit = 'cover';
    sparkle.style.pointerEvents = 'none';
    sparkle.style.boxShadow = `0 0 ${size}px rgba(59, 130, 246, 0.6)`;
    
    container.appendChild(sparkle);
    
    const startTime = Date.now();
    const duration = 800;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;
      
      if (progress >= 1) {
        sparkle.remove();
        return;
      }
      
      const currentX = x + Math.cos(angle) * distance * progress;
      const currentY = y + Math.sin(angle) * distance * progress - 50 * progress * progress;
      
      sparkle.style.left = `${currentX}px`;
      sparkle.style.top = `${currentY}px`;
      sparkle.style.opacity = 1 - progress;
      sparkle.style.transform = `scale(${1 - progress * 0.5}) rotate(${progress * 360}deg)`;
      
      requestAnimationFrame(animate);
    };
    
    requestAnimationFrame(animate);
  }
  
  setTimeout(() => {
    container.remove();
  }, 1000);
};

// Number counter animation
export const animateNumber = (element, from, to, duration = 1000, prefix = '', suffix = '') => {
  if (!element) return;
  
  const startTime = Date.now();
  const difference = to - from;
  
  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Easing function (ease-out)
    const easeOut = 1 - Math.pow(1 - progress, 3);
    const current = Math.floor(from + difference * easeOut);
    
    element.textContent = `${prefix}${current.toLocaleString()}${suffix}`;
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      element.textContent = `${prefix}${to.toLocaleString()}${suffix}`;
    }
  };
  
  animate();
};

// Particle burst effect
export const createParticleBurst = (element, options = {}) => {
  const {
    particleCount = 30,
    colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
    speed = 200,
    gravity = 0.5,
    duration = 1500
  } = options;
  
  if (!element) return;
  
  const rect = element.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '9999';
  document.body.appendChild(container);
  
  for (let i = 0; i < particleCount; i++) {
    const particle = document.createElement('div');
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = Math.random() * 8 + 4;
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * speed + speed * 0.5;
    const rotationSpeed = Math.random() * 360 - 180;
    
    particle.style.position = 'absolute';
    particle.style.left = `${centerX}px`;
    particle.style.top = `${centerY}px`;
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.backgroundColor = color;
    particle.style.borderRadius = '50%';
    particle.style.pointerEvents = 'none';
    particle.style.boxShadow = `0 0 ${size}px ${color}`;
    
    container.appendChild(particle);
    
    const startTime = Date.now();
    let vx = Math.cos(angle) * velocity;
    let vy = Math.sin(angle) * velocity;
    let x = centerX;
    let y = centerY;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;
      
      if (progress >= 1) {
        particle.remove();
        return;
      }
      
      vy += gravity;
      x += vx * 0.016;
      y += vy * 0.016;
      
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.transform = `rotate(${rotationSpeed * progress}deg)`;
      particle.style.opacity = 1 - progress;
      
      requestAnimationFrame(animate);
    };
    
    requestAnimationFrame(animate);
  }
  
  setTimeout(() => {
    container.remove();
  }, duration);
};

// Success burst (combination effect)
export const createSuccessBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 50,
    colors: ['#10b981', '#34d399', '#6ee7b7'],
    speed: 300
  });
  createGlow(element, '#10b981', 1000);
  createPulse(element, 1.15, 400);
  soundEffects.playSuccess();
};

// Achievement burst
export const createAchievementBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 60,
    colors: ['#f59e0b', '#fbbf24', '#fcd34d', '#fef3c7'],
    speed: 350
  });
  createSparkle(element, 15);
  createGlow(element, '#f59e0b', 1500);
  soundEffects.playAchievement();
};

// Level up burst
export const createLevelUpBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 80,
    colors: ['#3b82f6', '#60a5fa', '#93c5fd', '#dbeafe'],
    speed: 400
  });
  createSparkle(element, 20);
  createGlow(element, '#3b82f6', 2000);
  createScreenShake(element, 3, 500);
  soundEffects.playLevelUp();
};

// Coin burst
export const createCoinBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 40,
    colors: ['#fbbf24', '#f59e0b', '#fcd34d'],
    speed: 250
  });
  createGlow(element, '#fbbf24', 800);
  soundEffects.playCoin();
};

// Win burst
export const createWinBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 100,
    colors: ['#10b981', '#34d399', '#6ee7b7', '#fbbf24', '#f59e0b'],
    speed: 500
  });
  createSparkle(element, 25);
  createGlow(element, '#10b981', 2000);
  createScreenShake(element, 5, 600);
  soundEffects.playWin();
};

// Correct answer burst
export const createCorrectBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 30,
    colors: ['#10b981', '#34d399'],
    speed: 250
  });
  createSparkle(element, 8);
  createGlow(element, '#10b981', 800);
  soundEffects.playCorrect();
};

// Wrong answer burst
export const createWrongBurst = (element) => {
  createParticleBurst(element, {
    particleCount: 20,
    colors: ['#ef4444', '#f87171'],
    speed: 200
  });
  createScreenShake(element, 2, 300);
  soundEffects.playWrong();
};

// Text reveal animation
export const createTextReveal = (element, text, delay = 0) => {
  if (!element) return;
  
  setTimeout(() => {
    element.textContent = '';
    element.style.opacity = '0';
    element.style.transform = 'translateY(20px)';
    element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    
    setTimeout(() => {
      element.textContent = text;
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    }, 50);
  }, delay);
};

// Shimmer effect
export const createShimmer = (element, duration = 2000) => {
  if (!element) return;
  
  const shimmer = document.createElement('div');
  shimmer.style.position = 'absolute';
  shimmer.style.top = '0';
  shimmer.style.left = '-100%';
  shimmer.style.width = '100%';
  shimmer.style.height = '100%';
  shimmer.style.background = 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)';
  shimmer.style.animation = `shimmer ${duration}ms ease-in-out`;
  shimmer.style.pointerEvents = 'none';
  
  element.style.position = 'relative';
  element.style.overflow = 'hidden';
  element.appendChild(shimmer);
  
  setTimeout(() => {
    shimmer.remove();
  }, duration);
};

// Add shimmer animation to stylesheet if not exists
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes shimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
  `;
  if (!document.querySelector('style[data-shimmer]')) {
    style.setAttribute('data-shimmer', 'true');
    document.head.appendChild(style);
  }
}

export default {
  createScreenShake,
  createGlow,
  createPulse,
  createRipple,
  createSparkle,
  animateNumber,
  createParticleBurst,
  createSuccessBurst,
  createAchievementBurst,
  createLevelUpBurst,
  createCoinBurst,
  createWinBurst,
  createCorrectBurst,
  createWrongBurst,
  createTextReveal,
  createShimmer
};

